import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { ROLE_KEPALA_RUANGAN } from "@/lib/roles";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { InlineHint, PageEmptyState } from "@/components/PageStates";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FileText, Download, Printer, ChevronDown, ChevronRight, CheckCircle2 } from "lucide-react";
import ProofImageGallery from "@/components/ProofImageGallery";
import ProofStatusBadge from "@/components/ProofStatusBadge";
import { PageHero, PageShell } from "@/components/PageShell";
import { getTodayInBusinessTz, toDateInputValue } from "@/lib/date";
import SearchableDropdown from "@/components/SearchableDropdown";
import { getKategoriIcon, getKategoriTheme } from "@/lib/categoryPresentation";
import { cn } from "@/lib/utils";
import {
  hasSupplierNpwp,
  getTaxBreakdownFromTransaksi,
} from "@/lib/barangMasukTax";
import { formatRupiah, formatStockValue } from "@/lib/currency";
import { buildProofImageSrc, getTransactionProofImageUrls } from "@/lib/proofImages";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const actionPanelToneClasses = {
  slate: "border-slate-200 bg-slate-50/70",
  sky: "border-sky-200 bg-sky-50/70",
  emerald: "border-emerald-200 bg-emerald-50/70",
};

const summaryToneClasses = {
  emerald: "border-emerald-200 bg-gradient-to-br from-emerald-50 to-white text-emerald-900",
  sky: "border-sky-200 bg-gradient-to-br from-sky-50 to-white text-sky-900",
  amber: "border-amber-200 bg-gradient-to-br from-amber-50 to-white text-amber-900",
};

const ReportActionPanel = ({ title, description, tone = "slate", children, className }) => (
  <div className={cn("rounded-2xl border p-4 shadow-sm", actionPanelToneClasses[tone] || actionPanelToneClasses.slate, className)}>
    <div className="mb-3 space-y-1">
      <p className="font-manrope text-sm font-bold text-slate-900">{title}</p>
      {description ? <p className="text-xs text-muted-foreground">{description}</p> : null}
    </div>
    <div className="grid gap-2 [&>*]:w-full [&>*]:justify-start [&>*]:px-4">{children}</div>
  </div>
);

const ReportSummaryCard = ({ label, value, tone = "emerald", onClick, hint }) => {
  const Component = onClick ? "button" : "div";

  return (
    <Component
      type={onClick ? "button" : undefined}
      onClick={onClick}
      className={cn(
        "rounded-2xl border p-6 text-left shadow-sm",
        summaryToneClasses[tone] || summaryToneClasses.emerald,
        onClick ? "transition-transform hover:-translate-y-0.5 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2" : null
      )}
    >
      <p className="mb-2 text-sm font-manrope font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </p>
      <p className="text-2xl font-manrope font-bold">{value}</p>
      {hint ? <p className="mt-2 text-xs font-medium text-muted-foreground">{hint}</p> : null}
    </Component>
  );
};

const SpjBadge = ({ spjStatus, className = "" }) => {
  const isSpj = Boolean(spjStatus);

  return (
    <span
      className={[
        "inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-medium",
        isSpj
          ? "border-emerald-200 bg-emerald-100 text-emerald-800"
          : "border-slate-200 bg-slate-100 text-slate-600",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {isSpj ? <CheckCircle2 size={12} /> : null}
      {isSpj ? "SPJ" : "Belum SPJ"}
    </span>
  );
};

const LaporanPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canManageSpj = user?.role === ROLE_KEPALA_RUANGAN;
  const [laporanHarian, setLaporanHarian] = useState(null);
  const [laporanBulanan, setLaporanBulanan] = useState(null);
  const [laporanTahunan, setLaporanTahunan] = useState(null);
  const [activeTahunanKategori, setActiveTahunanKategori] = useState("all");
  const [loading, setLoading] = useState(false);
  const [tanggal, setTanggal] = useState(
    getTodayInBusinessTz()
  );
  const [bulan, setBulan] = useState(new Date().getMonth() + 1);
  const [tahun, setTahun] = useState(new Date().getFullYear());
  const [suppliers, setSuppliers] = useState([]);
  const [barangs, setBarangs] = useState([]);
  const [formatFotoModeHarian, setFormatFotoModeHarian] = useState("harian_multi_supplier");
  const [formatFotoModeBulanan, setFormatFotoModeBulanan] = useState("harian_multi_supplier");
  const [selectedSupplierIdHarian, setSelectedSupplierIdHarian] = useState("");
  const [selectedSupplierIdBulanan, setSelectedSupplierIdBulanan] = useState("");
  const [selectedSpjStatusBulanan, setSelectedSpjStatusBulanan] = useState("all");
  const [selectedBarangHarian, setSelectedBarangHarian] = useState("");
  const [searchBarangHarian, setSearchBarangHarian] = useState("");
  const [kartuStokHarian, setKartuStokHarian] = useState(null);
  const [loadingKartuStok, setLoadingKartuStok] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedTransaksiType, setSelectedTransaksiType] = useState("");
  const [selectedTransaksiDetail, setSelectedTransaksiDetail] = useState(null);
  const [downloadingNotaId, setDownloadingNotaId] = useState("");
  const [printingNotaId, setPrintingNotaId] = useState("");
  const [activeBulananKategori, setActiveBulananKategori] = useState("");
  const [spjUpdatingId, setSpjUpdatingId] = useState("");

  const getBarangName = (barangId) => {
    const barang = barangs.find((b) => b.barang_id === barangId);
    return barang ? barang.nama_barang : "-";
  };

  const getBarangSatuan = (barangId) => {
    const barang = barangs.find((b) => b.barang_id === barangId);
    return barang ? barang.satuan : "";
  };

  const getItemQtyWithSatuan = (item) => {
    const qtyDisplay = item?.qty_input ?? item?.qty ?? 0;
    const satuanDisplay = item?.satuan_input || getBarangSatuan(item?.barang_id) || "-";
    return `${qtyDisplay} ${satuanDisplay}`;
  };

  const getSupplierName = (supplierId) => {
    const supplier = suppliers.find((s) => s.supplier_id === supplierId);
    return supplier ? supplier.nama_supplier : "-";
  };

  const formatDateTimeId = (tanggalValue) => {
    if (!tanggalValue) {
      return "-";
    }

    const tanggalObj = new Date(tanggalValue);
    if (Number.isNaN(tanggalObj.getTime())) {
      return "-";
    }

    return tanggalObj.toLocaleDateString("id-ID", {
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatDateId = (tanggalValue) => {
    if (!tanggalValue) {
      return "-";
    }

    const tanggalObj = new Date(tanggalValue);
    if (Number.isNaN(tanggalObj.getTime())) {
      return "-";
    }

    return tanggalObj.toLocaleDateString("id-ID", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const getSupplierById = (supplierId) =>
    suppliers.find((supplier) => supplier.supplier_id === supplierId);

  const getTransaksiTaxBreakdown = (transaksi) => {
    const supplier = getSupplierById(transaksi?.supplier_id);
    const supplierHasNpwpValue =
      typeof transaksi?.supplier_memiliki_npwp === "boolean"
        ? transaksi.supplier_memiliki_npwp
        : hasSupplierNpwp(supplier);

    return {
      supplierHasNpwp: supplierHasNpwpValue,
      ...getTaxBreakdownFromTransaksi(transaksi, supplierHasNpwpValue),
    };
  };

  const getDisplayMasukTotalBelanja = (transaksi) => Number(transaksi?.total_belanja ?? 0);

  const bulananSpjSummary = useMemo(() => {
    const sections = laporanBulanan?.barang_masuk_sections || [];
    let totalTransaksi = 0;
    let spjSelesai = 0;
    let belumSpj = 0;
    let totalBelanjaSpj = 0;
    let totalBelanjaBelumSpj = 0;

    sections.forEach((section) => {
      (section.supplier_sections || []).forEach((supplierSection) => {
        (supplierSection.transaksi || []).forEach((transaksiItem) => {
          totalTransaksi += 1;
          const totalBelanja = getDisplayMasukTotalBelanja(transaksiItem);
          if (transaksiItem?.spj_status) {
            spjSelesai += 1;
            totalBelanjaSpj += totalBelanja;
          } else {
            belumSpj += 1;
            totalBelanjaBelumSpj += totalBelanja;
          }
        });
      });
    });

    const completionRate = totalTransaksi > 0 ? Math.round((spjSelesai / totalTransaksi) * 100) : 0;

    return {
      totalTransaksi,
      spjSelesai,
      belumSpj,
      totalBelanjaSpj,
      totalBelanjaBelumSpj,
      completionRate,
    };
  }, [laporanBulanan]);

  const getSpjStatusQueryValue = (value) => {
    if (value === "spj") {
      return true;
    }
    if (value === "belum_spj") {
      return false;
    }
    return undefined;
  };

  const getDisplayMasukSectionTotalBelanja = (supplierSections = []) =>
    supplierSections.reduce(
      (sectionTotal, supplierSection) =>
        sectionTotal +
        (supplierSection.transaksi || []).reduce(
          (transaksiTotal, transaksiItem) => transaksiTotal + getDisplayMasukTotalBelanja(transaksiItem),
          0
        ),
      0
    );

  const getDisplayMasukCategorySectionTotalBelanja = (items = []) =>
    items.reduce((sum, item) => sum + Number(item.total_belanja || 0), 0);

  const getDisplayMasukCategoryAverageHarga = (item) => {
    const storedAverage = Number(item?.harga_rata_rata ?? item?.harga_satuan ?? 0);
    if (Number.isFinite(storedAverage) && storedAverage > 0) {
      return storedAverage;
    }

    const totalQty = Number(item?.total_qty ?? 0);
    if (totalQty > 0) {
      return Number(item?.total_belanja ?? 0) / totalQty;
    }

    return 0;
  };

  const selectedTransaksiTaxBreakdown =
    selectedTransaksiType === "masuk" && selectedTransaksiDetail
      ? getTransaksiTaxBreakdown(selectedTransaksiDetail)
      : null;

  const selectedTransaksiDisplaySummary = selectedTransaksiDetail
    ? {
        subtotalSebelumPajak: Number(selectedTransaksiDetail.subtotal_sebelum_pajak ?? 0),
        nilaiPajak: Number(selectedTransaksiDetail.nilai_pajak ?? 0),
        totalBelanja: Number(selectedTransaksiDetail.total_belanja ?? 0),
        tarifPajakPersen: Number(selectedTransaksiDetail.tarif_pajak_persen ?? 0),
      }
    : {
        subtotalSebelumPajak: 0,
        nilaiPajak: 0,
        totalBelanja: 0,
        tarifPajakPersen: 0,
      };
  const selectedTransaksiDisplayItems = selectedTransaksiDetail?.items || [];
  const selectedTransaksiFotoGalleryItems = useMemo(
    () =>
      getTransactionProofImageUrls(selectedTransaksiDetail).map((url, index) => {
        const imageSrc = buildProofImageSrc(url);
        return {
          id: `${selectedTransaksiType || "laporan"}-${selectedTransaksiDetail?.pembelian_id || selectedTransaksiDetail?.keluar_id || index}`,
          src: imageSrc,
          href: imageSrc,
          alt: `Foto bukti transaksi ${index + 1}`,
          label: `Foto ${index + 1}`,
        };
      }),
    [selectedTransaksiDetail, selectedTransaksiType]
  );

  const handleOpenTransaksiDetail = (type, transaksiItem) => {
    setSelectedTransaksiType(type);
    setSelectedTransaksiDetail(transaksiItem);
    setDetailDialogOpen(true);
  };

  const handleDetailDialogChange = (open) => {
    setDetailDialogOpen(open);
    if (!open) {
      setSelectedTransaksiType("");
      setSelectedTransaksiDetail(null);
    }
  };

  const toggleBulananKategori = (kategoriKey) => {
    setActiveBulananKategori((current) => (current === kategoriKey ? "" : kategoriKey));
  };

  const updateTransaksiSpjInLaporanState = (pembelianId, updatedTransaksi) => {
    const patchTransaksiInSections = (sections) =>
      (sections || []).map((section) => ({
        ...section,
        supplier_sections: (section.supplier_sections || []).map((supplierSection) => ({
          ...supplierSection,
          transaksi: (supplierSection.transaksi || []).map((t) =>
            t.pembelian_id === pembelianId ? updatedTransaksi : t
          ),
        })),
      }));

    setLaporanBulanan((prev) =>
      prev
        ? { ...prev, barang_masuk_sections: patchTransaksiInSections(prev.barang_masuk_sections) }
        : prev
    );
    setLaporanHarian((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        barang_masuk: (prev.barang_masuk || []).map((t) =>
          t.pembelian_id === pembelianId ? updatedTransaksi : t
        ),
      };
    });
  };

  const handleToggleSpjStatus = async (transaksiItem) => {
    const pembelianId = transaksiItem?.pembelian_id;
    if (!canManageSpj || !pembelianId || spjUpdatingId === pembelianId) return;

    setSpjUpdatingId(pembelianId);
    try {
      const response = await axios.patch(`${API}/barang-masuk/${pembelianId}/spj`);
      const updatedTransaksi = response.data;
      setSelectedTransaksiDetail((prev) =>
        prev?.pembelian_id === pembelianId ? updatedTransaksi : prev
      );
      updateTransaksiSpjInLaporanState(pembelianId, updatedTransaksi);
      toast.success(updatedTransaksi.spj_status ? "Transaksi ditandai SPJ" : "Status SPJ dibatalkan");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Gagal memperbarui status SPJ");
    } finally {
      setSpjUpdatingId("");
    }
  };

  useEffect(() => {
    const fetchSuppliersAndBarang = async () => {
      try {
        const [supplierRes, barangRes] = await Promise.all([
          axios.get(`${API}/supplier`),
          axios.get(`${API}/barang`),
        ]);
        setSuppliers(supplierRes.data.filter((s) => s.aktif));
        setBarangs(barangRes.data.filter((b) => b.aktif !== false));
      } catch {
        setSuppliers([]);
        setBarangs([]);
      }
    };
    fetchSuppliersAndBarang();
  }, []);

  const fetchLaporanHarian = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/laporan/harian?tanggal=${tanggal}`
      );
      setLaporanHarian(response.data);
    } catch (error) {
      toast.error("Gagal memuat laporan harian");
    } finally {
      setLoading(false);
    }
  };

  const fetchLaporanBulanan = async () => {
    if (formatFotoModeBulanan === "supplier_detail" && !selectedSupplierIdBulanan) {
      toast.error("Pilih supplier untuk mode detail supplier");
      return;
    }

    const { tanggalMulai, tanggalAkhir } = getDateRangeByTipe("bulanan");
    setLoading(true);
    try {
      const response = await axios.get(`${API}/laporan/bulanan/preview`, {
        params: {
          tanggal_mulai: tanggalMulai,
          tanggal_akhir: tanggalAkhir,
          mode: formatFotoModeBulanan,
          supplier_id: formatFotoModeBulanan === "supplier_detail" ? selectedSupplierIdBulanan : undefined,
          spj_status: getSpjStatusQueryValue(selectedSpjStatusBulanan),
        },
      });
      setLaporanBulanan(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Gagal memuat laporan bulanan");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenLaporanBarangKeluarBulanan = () => {
    const params = new URLSearchParams({
      bulan: String(bulan),
      tahun: String(tahun),
    });
    navigate(`/laporan/barang-keluar?${params.toString()}`);
  };

  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const triggerBrowserDownload = (response, fallbackName) => {
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    const disposition = response.headers?.["content-disposition"] || "";
    const match = disposition.match(/filename=\"?([^\";]+)\"?/i);
    const filename = match?.[1] || fallbackName;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const waitExportAndDownload = async (jobId, fallbackName) => {
    for (let i = 0; i < 80; i++) {
      const statusRes = await axios.get(`${API}/laporan/export-async/${jobId}`);
      const status = statusRes.data?.status;
      if (status === "done") {
        const fileRes = await axios.get(`${API}/laporan/export-async/${jobId}/download`, {
          responseType: "blob",
        });
        triggerBrowserDownload(fileRes, fallbackName);
        return;
      }
      if (status === "failed") {
        throw new Error(statusRes.data?.error || "Proses export gagal");
      }
      await sleep(1500);
    }
    throw new Error("Export membutuhkan waktu lebih lama dari batas tunggu");
  };

  const getFilenameFromDisposition = (disposition, fallbackName) => {
    const match = String(disposition || "").match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
    if (!match?.[1]) {
      return fallbackName;
    }
    const raw = match[1].replace(/["']/g, "").trim();
    try {
      return decodeURIComponent(raw);
    } catch {
      return raw;
    }
  };

  const getNotaFilenameByTanggal = (tanggalValue) => {
    if (!tanggalValue) {
      return "Nota.pdf";
    }

    const tanggalObj = new Date(tanggalValue);
    if (Number.isNaN(tanggalObj.getTime())) {
      return "Nota.pdf";
    }

    const tanggalLabel = tanggalObj
      .toLocaleDateString("id-ID", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
      .toLowerCase();

    return `Nota (${tanggalLabel}).pdf`;
  };

  const extractAxiosBlobErrorMessage = async (error, fallbackMessage) => {
    const rawData = error?.response?.data;
    if (rawData && typeof rawData === "object" && !(rawData instanceof Blob)) {
      if (typeof rawData.detail === "string" && rawData.detail.trim()) {
        return rawData.detail;
      }
      if (typeof rawData.message === "string" && rawData.message.trim()) {
        return rawData.message;
      }
    }

    if (rawData instanceof Blob) {
      try {
        const text = await rawData.text();
        if (text) {
          try {
            const parsed = JSON.parse(text);
            if (typeof parsed?.detail === "string" && parsed.detail.trim()) {
              return parsed.detail;
            }
            if (typeof parsed?.message === "string" && parsed.message.trim()) {
              return parsed.message;
            }
          } catch {
            const compactText = text.trim();
            if (compactText) {
              return compactText;
            }
          }
        }
      } catch {
        // no-op
      }
    }

    const status = error?.response?.status;
    if (status === 404) {
      return "Endpoint nota belum tersedia di backend. Restart backend lalu coba lagi.";
    }
    return status ? `${fallbackMessage} (HTTP ${status})` : fallbackMessage;
  };

  const fetchNotaPdfResponse = async (pembelianId) =>
    axios.get(`${API}/barang-masuk/${pembelianId}/nota-pdf?t=${Date.now()}`, {
      responseType: "blob",
      headers: {
        "Cache-Control": "no-cache",
        Pragma: "no-cache",
      },
    });

  const triggerDownloadFromBlobResponse = (response, fallbackName) => {
    const blob = response.data instanceof Blob
      ? response.data
      : new Blob([response.data], { type: "application/pdf" });
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobUrl;
    const filename = getFilenameFromDisposition(
      response.headers?.["content-disposition"],
      fallbackName
    );
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    link.parentNode?.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
  };

  const handleDownloadNota = async (transaksiItem) => {
    const pembelianId = transaksiItem?.pembelian_id;
    if (!pembelianId) {
      return;
    }

    const fallbackFilename = getNotaFilenameByTanggal(transaksiItem?.tanggal);

    setDownloadingNotaId(pembelianId);
    try {
      const response = await fetchNotaPdfResponse(pembelianId);
      triggerDownloadFromBlobResponse(response, fallbackFilename);
      toast.success("Nota berhasil didownload");
    } catch (error) {
      const errorMessage = await extractAxiosBlobErrorMessage(error, "Gagal download nota");
      toast.error(errorMessage);
    } finally {
      setDownloadingNotaId("");
    }
  };

  const handlePrintNota = async (pembelianId) => {
    if (!pembelianId) {
      return;
    }
    setPrintingNotaId(pembelianId);
    try {
      const response = await fetchNotaPdfResponse(pembelianId);
      const blob = response.data instanceof Blob
        ? response.data
        : new Blob([response.data], { type: "application/pdf" });
      const blobUrl = window.URL.createObjectURL(blob);
      const iframe = document.createElement("iframe");
      iframe.style.position = "fixed";
      iframe.style.width = "0";
      iframe.style.height = "0";
      iframe.style.border = "0";
      iframe.style.right = "0";
      iframe.style.bottom = "0";
      iframe.onload = () => {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
        setTimeout(() => {
          window.URL.revokeObjectURL(blobUrl);
          iframe.parentNode?.removeChild(iframe);
        }, 1500);
      };
      iframe.src = blobUrl;
      document.body.appendChild(iframe);
      toast.success("Dialog print nota dibuka");
    } catch (error) {
      const errorMessage = await extractAxiosBlobErrorMessage(error, "Gagal print nota");
      toast.error(errorMessage);
    } finally {
      setPrintingNotaId("");
    }
  };

  const handleExportExcel = async (tipe) => {
    if (exporting) {
      toast.info("Masih ada proses export yang berjalan");
      return;
    }

    const { tanggalMulai, tanggalAkhir } = getDateRangeByTipe(tipe);
    const activeMode = tipe === "bulanan" ? formatFotoModeBulanan : formatFotoModeHarian;
    const activeSupplierId = tipe === "bulanan" ? selectedSupplierIdBulanan : selectedSupplierIdHarian;
    const activeSpjStatus = tipe === "bulanan" ? getSpjStatusQueryValue(selectedSpjStatusBulanan) : undefined;

    if (activeMode === "supplier_detail" && !activeSupplierId) {
      toast.error("Pilih supplier untuk mode detail supplier");
      return;
    }

    try {
      setExporting(true);
      const jobRes = await axios.post(`${API}/laporan/export-excel/async`, {
        tipe,
        tanggal_mulai: tanggalMulai,
        tanggal_akhir: tanggalAkhir,
        mode: activeMode,
        supplier_id: activeMode === "supplier_detail" ? activeSupplierId : null,
        spj_status: activeSpjStatus,
      });
      toast.info("Export sedang diproses, mohon tunggu...");
      await waitExportAndDownload(
        jobRes.data.job_id,
        getExportFilename({
          mode: activeMode,
          supplierId: activeSupplierId,
          startDateInput: tanggalMulai,
          endDateInput: tanggalAkhir,
          extension: "xlsx",
        })
      );
      toast.success("Excel berhasil didownload");
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || "Gagal export Excel");
    } finally {
      setExporting(false);
    }
  };

  const fetchLaporanTahunan = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/laporan/tahunan?tahun=${tahun}`);
      setLaporanTahunan(response.data);
      setActiveTahunanKategori("all");
    } catch (error) {
      toast.error("Gagal memuat laporan tahunan");
    } finally {
      setLoading(false);
    }
  };

  const getDateRangeByTipe = (tipe) => {
    if (tipe === "harian") {
      return { tanggalMulai: tanggal, tanggalAkhir: tanggal };
    }

    if (tipe === "mingguan") {
      const startDate = new Date(tanggal);
      const endDate = new Date(startDate);
      endDate.setDate(startDate.getDate() + 6);
      return {
        tanggalMulai: toDateInputValue(startDate),
        tanggalAkhir: toDateInputValue(endDate),
      };
    }

    const startDate = new Date(tahun, bulan - 1, 1);
    const endDate = new Date(tahun, bulan, 0);
    return {
      tanggalMulai: toDateInputValue(startDate),
      tanggalAkhir: toDateInputValue(endDate),
    };
  };

  const sanitizeFilenamePart = (value) =>
    String(value || "")
      .replace(/[\\/:*?"<>|]+/g, " ")
      .replace(/\s+/g, " ")
      .trim();

  const parseDateInputParts = (value) => {
    const [year, month, day] = String(value || "").split("-").map((part) => Number(part));
    if (!year || !month || !day) {
      return null;
    }

    const date = new Date(year, month - 1, day);
    if (Number.isNaN(date.getTime())) {
      return null;
    }

    return { year, month, day, date };
  };

  const buildExportPeriodLabel = (startDateInput, endDateInput) => {
    const startParts = parseDateInputParts(startDateInput);
    const endParts = parseDateInputParts(endDateInput);
    if (!startParts || !endParts) {
      return "";
    }

    const startMonth = startParts.date
      .toLocaleDateString("id-ID", { month: "long" })
      .toLowerCase();
    const endMonth = endParts.date
      .toLocaleDateString("id-ID", { month: "long" })
      .toLowerCase();

    if (startParts.year === endParts.year && startParts.month === endParts.month) {
      if (startParts.day === endParts.day) {
        return `${startMonth} ${startParts.day} ${startParts.year}`;
      }
      return `${startMonth} ${startParts.day}-${endParts.day} ${startParts.year}`;
    }

    if (startParts.year === endParts.year) {
      return `${startMonth} ${startParts.day} - ${endMonth} ${endParts.day} ${startParts.year}`;
    }

    return `${startMonth} ${startParts.day} ${startParts.year} - ${endMonth} ${endParts.day} ${endParts.year}`;
  };

  const getExportFilename = ({ mode, supplierId, startDateInput, endDateInput, extension }) => {
    let prefix = "Laporan";
    if (mode === "supplier_detail") {
      prefix = sanitizeFilenamePart(getSupplierName(supplierId)) || "Detail per Supplier";
    } else if (mode === "per_kategori") {
      prefix = "Per Kategori";
    } else if (mode === "harian_multi_supplier") {
      prefix = "Harian Multi Supplier";
    }

    const period = buildExportPeriodLabel(startDateInput, endDateInput);
    return `${prefix} ${period}.${extension}`.trim();
  };

  const handleExportFormatFoto = async (tipe, output) => {
    if (exporting) {
      toast.info("Masih ada proses export yang berjalan");
      return;
    }

    const { tanggalMulai, tanggalAkhir } = getDateRangeByTipe(tipe);
    const extension = output === "pdf" ? "pdf" : "xlsx";
    const activeMode = tipe === "bulanan" ? formatFotoModeBulanan : formatFotoModeHarian;
    const activeSupplierId = tipe === "bulanan" ? selectedSupplierIdBulanan : selectedSupplierIdHarian;
    const activeSpjStatus = tipe === "bulanan" ? getSpjStatusQueryValue(selectedSpjStatusBulanan) : undefined;

    if (activeMode === "supplier_detail" && !activeSupplierId) {
      toast.error("Pilih supplier untuk mode detail supplier");
      return;
    }

    try {
      setExporting(true);
      const jobRes = await axios.post(`${API}/laporan/export-format-foto/async`, {
        tanggal_mulai: tanggalMulai,
        tanggal_akhir: tanggalAkhir,
        output,
        mode: activeMode,
        supplier_id: activeMode === "supplier_detail" ? activeSupplierId : null,
        spj_status: activeSpjStatus,
      });
      toast.info("Export format foto sedang diproses, mohon tunggu...");
      await waitExportAndDownload(
        jobRes.data.job_id,
        getExportFilename({
          mode: activeMode,
          supplierId: activeSupplierId,
          startDateInput: tanggalMulai,
          endDateInput: tanggalAkhir,
          extension,
        })
      );
      toast.success(`Laporan format foto (${output.toUpperCase()}) berhasil didownload`);
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || `Gagal export format foto (${output.toUpperCase()})`);
    } finally {
      setExporting(false);
    }
  };

  const handleExportTahunanExcel = async () => {
    if (exporting) {
      toast.info("Masih ada proses export yang berjalan");
      return;
    }

    try {
      setExporting(true);
      const jobRes = await axios.post(`${API}/laporan/tahunan/export-excel/async`, {
        tahun,
      });
      toast.info("Export laporan tahunan sedang diproses, mohon tunggu...");
      await waitExportAndDownload(jobRes.data.job_id, `Laporan_Tahunan_${tahun}.xlsx`);
      toast.success("Laporan tahunan berhasil didownload");
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || "Gagal export laporan tahunan");
    } finally {
      setExporting(false);
    }
  };

  const handleFetchKartuStok = async (barangIdOverride) => {
    const barangId = barangIdOverride || selectedBarangHarian;
    if (!barangId) {
      toast.error("Pilih barang terlebih dahulu");
      return;
    }

    const { tanggalMulai, tanggalAkhir } = getDateRangeByTipe("harian");
    setLoadingKartuStok(true);
    try {
      const response = await axios.get(
        `${API}/laporan/kartu-stok?barang_id=${barangId}&tanggal_mulai=${tanggalMulai}&tanggal_akhir=${tanggalAkhir}`
      );
      setSelectedBarangHarian(barangId);
      setKartuStokHarian(response.data);
    } catch (error) {
      toast.error("Gagal memuat kartu stok");
    } finally {
      setLoadingKartuStok(false);
    }
  };

  const renderKartuStok = (kartuStok) => {
    if (!kartuStok) return null;

    return (
      <div className="rounded-lg border border-cyan-200 bg-cyan-50/70 p-6 shadow-sm space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div>
            <p className="text-xs text-muted-foreground">Stok Awal</p>
            <p className="font-manrope font-bold">{formatStockValue(kartuStok.saldo_awal)} {kartuStok.satuan_dasar}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Total Masuk</p>
            <p className="font-manrope font-bold text-green-700">{formatStockValue(kartuStok.total_masuk)} {kartuStok.satuan_dasar}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Total Keluar</p>
            <p className="font-manrope font-bold text-orange-700">{formatStockValue(kartuStok.total_keluar)} {kartuStok.satuan_dasar}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Penyesuaian</p>
            <p className="font-manrope font-bold text-sky-700">{formatStockValue(kartuStok.total_penyesuaian)} {kartuStok.satuan_dasar}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Stok Akhir</p>
            <p className="font-manrope font-bold text-primary">{formatStockValue(kartuStok.saldo_akhir)} {kartuStok.satuan_dasar}</p>
          </div>
        </div>

        <div className="space-y-3 md:hidden">
          {kartuStok.mutasi.length === 0 ? (
            <p className="rounded-xl border border-dashed border-cyan-200 bg-white/80 px-4 py-6 text-center text-sm text-muted-foreground">
              Tidak ada mutasi pada periode ini
            </p>
          ) : (
            kartuStok.mutasi.map((m, idx) => (
              <div key={idx} className="rounded-xl border border-cyan-200/80 bg-white/90 p-4 shadow-sm">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-medium text-slate-900">{new Date(m.tanggal).toLocaleString("id-ID")}</p>
                    <p className="text-xs text-muted-foreground">{m.petugas || "-"}</p>
                  </div>
                  <span className="rounded-full bg-cyan-100 px-2.5 py-1 text-xs font-semibold text-cyan-800">
                    {m.tipe}
                  </span>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">Qty Input</p>
                    <p>{formatStockValue(m.qty_input)} {m.satuan_input}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">Qty Dasar</p>
                    <p>{formatStockValue(m.qty_base)} {kartuStok.satuan_dasar}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">Saldo</p>
                    <p className="font-medium">{formatStockValue(m.saldo_berjalan)} {kartuStok.satuan_dasar}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">Referensi</p>
                    <p>{m.referensi || "-"}</p>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        <div className="hidden overflow-x-auto rounded-xl border border-cyan-200/80 bg-white/80 md:block">
          <table className="min-w-[820px] w-full">
            <thead>
              <tr className="border-b bg-cyan-100/80">
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Tanggal</th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Jenis</th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Qty Input</th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Qty Dasar</th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Saldo</th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Petugas</th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Ref</th>
              </tr>
            </thead>
            <tbody>
              {kartuStok.mutasi.length === 0 ? (
                <tr>
                  <td className="px-3 py-4 text-center text-muted-foreground" colSpan={7}>
                    Tidak ada mutasi pada periode ini
                  </td>
                </tr>
              ) : (
                kartuStok.mutasi.map((m, idx) => (
                  <tr key={idx} className="border-b hover:bg-muted/40">
                    <td className="px-3 py-2">{new Date(m.tanggal).toLocaleString("id-ID")}</td>
                    <td className="px-3 py-2">{m.tipe}</td>
                    <td className="px-3 py-2">{formatStockValue(m.qty_input)} {m.satuan_input}</td>
                    <td className="px-3 py-2">{formatStockValue(m.qty_base)} {kartuStok.satuan_dasar}</td>
                    <td className="px-3 py-2 font-medium">{formatStockValue(m.saldo_berjalan)} {kartuStok.satuan_dasar}</td>
                    <td className="px-3 py-2">{m.petugas}</td>
                    <td className="px-3 py-2">{m.referensi}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const tahunanKategoriAktif = laporanTahunan?.kategori_data?.find(
    (kategori) => kategori.kategori_id === activeTahunanKategori
  ) || null;

  const isBulananPerKategori = laporanBulanan?.filter?.mode === "per_kategori";
  const bulananKategoriSections = useMemo(
    () => laporanBulanan?.barang_masuk_kategori_sections || [],
    [laporanBulanan]
  );

  useEffect(() => {
    if (!isBulananPerKategori) {
      setActiveBulananKategori("");
      return;
    }

    const hasActiveKategori = bulananKategoriSections.some(
      (section) => (section.kategori_id || section.nama_kategori || "tanpa-kategori") === activeBulananKategori
    );

    if (!hasActiveKategori) {
      setActiveBulananKategori("");
    }
  }, [activeBulananKategori, isBulananPerKategori, bulananKategoriSections]);

  const tahunanKategoriTampil = laporanTahunan
    ? activeTahunanKategori === "all"
      ? laporanTahunan.kategori_data
      : tahunanKategoriAktif
        ? [tahunanKategoriAktif]
        : laporanTahunan.kategori_data
    : [];

  const tahunanTitle = tahunanKategoriAktif
    ? `Rekap Tahunan ${tahunanKategoriAktif.nama_kategori} (${laporanTahunan?.tahun})`
    : `Rekap Tahunan Semua Barang (${laporanTahunan?.tahun})`;

  const tahunanDescription = tahunanKategoriAktif
    ? `Menampilkan ringkasan bulanan khusus kategori ${tahunanKategoriAktif.nama_kategori}.`
    : "Pilih tab kategori untuk fokus ke satu kelompok bahan atau lihat semua kategori sekaligus.";

  return (
    <PageShell data-testid="laporan-page">
      <PageHero
        tone="sky"
        title="Laporan"
        eyebrow="Monitoring Dan Rekap"
        description="Akses laporan transaksi, kartu stok, dan export operasional dalam kerangka visual yang lebih seragam."
      />

      <Tabs defaultValue="harian" className="space-y-6">
        <TabsList className="grid w-full max-w-xl grid-cols-3 border border-sky-200 bg-white/95">
          <TabsTrigger
            value="harian"
            data-testid="tab-harian"
            className="text-slate-700 hover:bg-sky-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-md"
          >
            Laporan Harian
          </TabsTrigger>
          <TabsTrigger
            value="bulanan"
            data-testid="tab-bulanan"
            className="text-slate-700 hover:bg-sky-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-md"
          >
            Laporan Bulanan
          </TabsTrigger>
          <TabsTrigger
            value="tahunan"
            data-testid="tab-tahunan"
            className="text-slate-700 hover:bg-sky-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-md"
          >
            Laporan Tahunan
          </TabsTrigger>
        </TabsList>

        <TabsContent value="harian" className="space-y-6">
          <div className="rounded-2xl border border-sky-200 bg-white/95 p-6 shadow-sm">
            <h2 className="font-manrope font-bold text-xl mb-4">
              Filter Laporan Harian
            </h2>
            <InlineHint className="mb-4 border-sky-200/80 bg-sky-50/70">
              Gunakan filter ini untuk meninjau laporan harian lalu pilih format export yang sesuai kebutuhan administrasi.
            </InlineHint>
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
              <div className="space-y-2">
                <Label>Mode Format Foto</Label>
                <Select value={formatFotoModeHarian} onValueChange={setFormatFotoModeHarian}>
                  <SelectTrigger>
                    <SelectValue placeholder="Pilih mode" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="harian_multi_supplier">Harian Multi Supplier</SelectItem>
                    <SelectItem value="supplier_detail">Detail per Supplier</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {formatFotoModeHarian === "supplier_detail" && (
                <div className="space-y-2">
                  <Label>Supplier</Label>
                  <Select value={selectedSupplierIdHarian} onValueChange={setSelectedSupplierIdHarian}>
                    <SelectTrigger>
                      <SelectValue placeholder="Pilih supplier" />
                    </SelectTrigger>
                    <SelectContent>
                      {suppliers.map((supplier) => (
                        <SelectItem key={supplier.supplier_id} value={supplier.supplier_id}>
                          {supplier.nama_supplier}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="space-y-2">
                <Label>Tanggal</Label>
                <Input
                  data-testid="input-tanggal-harian"
                  type="date"
                  value={tanggal}
                  onChange={(e) => setTanggal(e.target.value)}
                />
              </div>
            </div>
            <div className="mt-4 grid gap-3 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
              <ReportActionPanel
                title="Aksi Laporan Harian"
                description="Tampilkan hasil di layar atau export rekap harian ke Excel standar."
                tone="sky"
              >
                <Button
                  data-testid="button-fetch-harian"
                  onClick={fetchLaporanHarian}
                  disabled={loading}
                >
                  <FileText size={16} className="mr-2" />
                  Tampilkan Laporan
                </Button>
                <Button
                  data-testid="button-export-harian"
                  variant="outline"
                  disabled={exporting}
                  onClick={() => handleExportExcel("harian")}
                >
                  <Download size={16} className="mr-2" />
                  Export Excel
                </Button>
              </ReportActionPanel>

              <ReportActionPanel
                title="Export Format SPJ"
                description="Gunakan format khusus untuk lampiran administrasi berbasis supplier atau mode foto."
                tone="emerald"
              >
                <Button
                  variant="outline"
                  disabled={exporting}
                  onClick={() => handleExportFormatFoto("harian", "excel")}
                >
                  <Download size={16} className="mr-2" />
                  Format SPJ Excel
                </Button>
                <Button
                  variant="outline"
                  disabled={exporting}
                  onClick={() => handleExportFormatFoto("harian", "pdf")}
                >
                  <Download size={16} className="mr-2" />
                  Format SPJ PDF
                </Button>
              </ReportActionPanel>
            </div>
          </div>

          {laporanHarian && (
            <div className="space-y-6">
              <div className="rounded-lg border border-emerald-200 bg-emerald-50/70 p-6 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-manrope font-bold text-xl">
                    Barang Masuk ({laporanHarian.barang_masuk.length})
                  </h3>
                </div>
                {laporanHarian.barang_masuk.length === 0 ? (
                  <PageEmptyState
                    compact
                    title="Tidak ada transaksi barang masuk"
                    description="Belum ada transaksi barang masuk pada tanggal yang dipilih."
                  />
                ) : (
                  <div className="space-y-4">
                    {laporanHarian.barang_masuk.map((t, idx) => (
                      <button
                        key={t.pembelian_id || `masuk-${idx}`}
                        type="button"
                        onClick={() => handleOpenTransaksiDetail("masuk", t)}
                        className="w-full rounded-md border p-4 bg-green-50/50 text-left transition-colors hover:bg-green-100/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="space-y-2">
                            <div>
                              <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-emerald-700/80">
                                Supplier
                              </p>
                              <p className="font-manrope text-lg font-bold text-emerald-950">
                                {getSupplierName(t.supplier_id)}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                                Total Belanja
                              </p>
                              <p className="font-medium text-slate-900">
                                {formatRupiah(getDisplayMasukTotalBelanja(t))}
                              </p>
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {formatDateTimeId(t.tanggal)}
                            </div>
                            {t.keterangan && (
                              <p className="text-xs text-muted-foreground whitespace-pre-line">
                                Catatan: {t.keterangan}
                              </p>
                            )}
                          </div>
                          <span className="text-xs font-semibold text-emerald-700">Lihat Detail</span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="rounded-lg border border-orange-200 bg-orange-50/70 p-6 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-manrope font-bold text-xl">
                    Barang Keluar ({laporanHarian.barang_keluar.length})
                  </h3>
                </div>
                {laporanHarian.barang_keluar.length === 0 ? (
                  <PageEmptyState
                    compact
                    title="Tidak ada transaksi barang keluar"
                    description="Belum ada transaksi barang keluar pada tanggal yang dipilih."
                  />
                ) : (
                  <div className="space-y-4">
                    {laporanHarian.barang_keluar.map((t, idx) => (
                      <button
                        key={t.keluar_id || `keluar-${idx}`}
                        type="button"
                        onClick={() => handleOpenTransaksiDetail("keluar", t)}
                        className="w-full rounded-md border p-4 bg-orange-50/50 text-left transition-colors hover:bg-orange-100/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <p className="font-medium mb-1">
                              {t.items.length} item keluar
                            </p>
                            <div className="text-sm text-muted-foreground">
                              Petugas: {t.petugas}
                              {t.keterangan && ` - ${t.keterangan}`}
                            </div>
                            <div className="text-xs text-muted-foreground mt-1">
                              {formatDateTimeId(t.tanggal)}
                            </div>
                          </div>
                          <span className="text-xs font-semibold text-orange-700">Lihat Detail</span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="rounded-lg border border-cyan-200 bg-cyan-50/70 p-6 shadow-sm space-y-4">
            <h3 className="font-manrope font-bold text-xl">Kartu Stok Barang (Harian)</h3>
            <div className="flex flex-col md:flex-row gap-3 md:items-end">
              <div className="flex-1 space-y-2">
                <Label>Pilih Barang</Label>
                <SearchableDropdown
                  value={selectedBarangHarian}
                  searchValue={searchBarangHarian}
                  onSearchValueChange={setSearchBarangHarian}
                  onValueChange={(value) => {
                    setSelectedBarangHarian(value);
                    setSearchBarangHarian("");
                  }}
                  selectedPlaceholder="Pilih barang"
                  emptyText="Tidak ada barang yang cocok"
                  items={barangs.map((barang) => ({
                    value: barang.barang_id,
                    label: barang.nama_barang,
                  }))}
                />
              </div>
              <Button onClick={() => handleFetchKartuStok()} disabled={loadingKartuStok}>
                <FileText size={16} className="mr-2" />
                Tampilkan Kartu Stok
              </Button>
            </div>
            {renderKartuStok(kartuStokHarian)}
          </div>
        </TabsContent>

        <TabsContent value="bulanan" className="space-y-6">
          <div className="rounded-2xl border border-sky-200 bg-white/95 p-6 shadow-sm">
            <h2 className="font-manrope font-bold text-xl mb-4">
              Filter Laporan Bulanan
            </h2>
            <InlineHint className="mb-4 border-sky-200/80 bg-sky-50/70">
              Preview bulanan dapat ditampilkan per supplier atau per kategori agar kepala ruangan lebih mudah membaca total belanja dan aktivitas transaksi pada periode terpilih.
            </InlineHint>
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-5">
              <div className="space-y-2">
                <Label>Mode Format Foto</Label>
                <Select value={formatFotoModeBulanan} onValueChange={setFormatFotoModeBulanan}>
                  <SelectTrigger>
                    <SelectValue placeholder="Pilih mode" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="harian_multi_supplier">Harian Multi Supplier</SelectItem>
                    <SelectItem value="supplier_detail">Detail per Supplier</SelectItem>
                    <SelectItem value="per_kategori">Per Kategori</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {formatFotoModeBulanan === "supplier_detail" && (
                <div className="space-y-2">
                  <Label>Supplier</Label>
                  <Select value={selectedSupplierIdBulanan} onValueChange={setSelectedSupplierIdBulanan}>
                    <SelectTrigger>
                      <SelectValue placeholder="Pilih supplier" />
                    </SelectTrigger>
                    <SelectContent>
                      {suppliers.map((supplier) => (
                        <SelectItem key={supplier.supplier_id} value={supplier.supplier_id}>
                          {supplier.nama_supplier}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="space-y-2">
                <Label>Status SPJ</Label>
                <Select value={selectedSpjStatusBulanan} onValueChange={setSelectedSpjStatusBulanan}>
                  <SelectTrigger>
                    <SelectValue placeholder="Semua status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Semua status</SelectItem>
                    <SelectItem value="spj">SPJ</SelectItem>
                    <SelectItem value="belum_spj">Belum SPJ</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Bulan</Label>
                <Input
                  data-testid="input-bulan"
                  type="number"
                  min="1"
                  max="12"
                  value={bulan}
                  onChange={(e) => setBulan(parseInt(e.target.value))}
                />
              </div>
              <div className="space-y-2">
                <Label>Tahun</Label>
                <Input
                  data-testid="input-tahun"
                  type="number"
                  value={tahun}
                  onChange={(e) => setTahun(parseInt(e.target.value))}
                />
              </div>
            </div>
            <div className="mt-4 grid gap-3 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
              <ReportActionPanel
                title="Aksi Laporan Bulanan"
                description="Tampilkan hasil di layar atau export rekap bulanan ke Excel standar."
                tone="sky"
              >
                <Button
                  data-testid="button-fetch-bulanan"
                  onClick={fetchLaporanBulanan}
                  disabled={loading}
                >
                  <FileText size={16} className="mr-2" />
                  Tampilkan Laporan
                </Button>
                <Button
                  data-testid="button-export-bulanan"
                  variant="outline"
                  disabled={exporting}
                  onClick={() => handleExportExcel("bulanan")}
                >
                  <Download size={16} className="mr-2" />
                  Export Excel
                </Button>
              </ReportActionPanel>

              <ReportActionPanel
                title="Export Format SPJ"
                description="Siapkan format excel atau pdf untuk pelaporan administrasi bulanan."
                tone="emerald"
              >
                <Button
                  variant="outline"
                  disabled={exporting}
                  onClick={() => handleExportFormatFoto("bulanan", "excel")}
                >
                  <Download size={16} className="mr-2" />
                  Format SPJ Excel
                </Button>
                <Button
                  variant="outline"
                  disabled={exporting}
                  onClick={() => handleExportFormatFoto("bulanan", "pdf")}
                >
                  <Download size={16} className="mr-2" />
                  Format SPJ PDF
                </Button>
              </ReportActionPanel>
            </div>
          </div>

          {laporanBulanan && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <ReportSummaryCard
                  label="Total Pembelian"
                  value={formatRupiah(laporanBulanan.summary.total_pembelian)}
                  tone="emerald"
                />
                <ReportSummaryCard
                  label={isBulananPerKategori ? "Kategori Belanja" : "Transaksi Barang Masuk"}
                  value={isBulananPerKategori
                    ? `${laporanBulanan.summary.jumlah_kategori_belanja || 0} kategori`
                    : `${laporanBulanan.summary.jumlah_transaksi_masuk} transaksi`}
                  tone="sky"
                  hint={isBulananPerKategori
                    ? "Akumulasi total pembelian barang masuk per kategori"
                    : undefined}
                />
                <ReportSummaryCard
                  label="Transaksi Barang Keluar"
                  value={`${laporanBulanan.summary.jumlah_transaksi_keluar} transaksi`}
                  tone="amber"
                  onClick={handleOpenLaporanBarangKeluarBulanan}
                  hint="Klik untuk membuka halaman detail barang keluar"
                />
              </div>

              <div className="rounded-2xl border border-emerald-200 bg-emerald-50/70 p-4 shadow-sm space-y-4">
                <div className="flex flex-col gap-1 md:flex-row md:items-end md:justify-between">
                  <div>
                    <h3 className="font-manrope text-lg font-bold text-emerald-950">Ringkasan SPJ</h3>
                    <p className="text-sm text-emerald-900/70">
                      Mengikuti transaksi barang masuk yang sedang ditampilkan.
                    </p>
                  </div>
                  <p className="text-sm font-medium text-emerald-800">
                    {bulananSpjSummary.spjSelesai} selesai · {bulananSpjSummary.belumSpj} belum
                  </p>
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <ReportSummaryCard
                    label="SPJ Selesai"
                    value={`${bulananSpjSummary.spjSelesai} transaksi`}
                    tone="emerald"
                    hint={`${formatRupiah(bulananSpjSummary.totalBelanjaSpj)} total belanja · ${bulananSpjSummary.completionRate}% dari transaksi`}
                  />
                  <ReportSummaryCard
                    label="Belum SPJ"
                    value={`${bulananSpjSummary.belumSpj} transaksi`}
                    tone="amber"
                    hint={`${formatRupiah(bulananSpjSummary.totalBelanjaBelumSpj)} total belanja`}
                  />
                </div>
              </div>

              {laporanBulanan.filter?.supplier_nama || laporanBulanan.filter?.spj_status_label ? (
                <InlineHint className="border-amber-200 bg-amber-50/80 text-amber-950">
                  {laporanBulanan.filter?.supplier_nama ? (
                    <>
                      Filter supplier aktif untuk section barang masuk: <span className="font-semibold">{laporanBulanan.filter.supplier_nama}</span>.
                    </>
                  ) : null}
                  {laporanBulanan.filter?.supplier_nama && laporanBulanan.filter?.spj_status_label ? " " : null}
                  {laporanBulanan.filter?.spj_status_label ? (
                    <>
                      Filter SPJ aktif: <span className="font-semibold">{laporanBulanan.filter.spj_status_label}</span>.
                    </>
                  ) : null}
                  {' '}
                  Section barang keluar tetap menampilkan seluruh transaksi keluar pada periode bulanan yang dipilih.
                </InlineHint>
              ) : null}

              {isBulananPerKategori ? (
                <div className="rounded-lg border border-sky-200 bg-sky-50/70 p-6 shadow-sm space-y-5">
                  <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                    <div>
                      <h3 className="font-manrope font-bold text-xl">Belanja per Kategori</h3>
                      <p className="text-sm text-muted-foreground">
                        Total belanja bulanan dikelompokkan per kategori agar kepala ruangan cepat melihat kategori dengan pengeluaran terbesar.
                      </p>
                    </div>
                    <span className="text-sm font-medium text-sky-800">
                      {laporanBulanan.summary.jumlah_kategori_belanja || 0} kategori
                    </span>
                  </div>

                  <InlineHint className="border-sky-200 bg-white/80 text-sky-950">
                    Total kategori dihitung dari subtotal item barang masuk pada periode ini, termasuk pembagian pajak item bila transaksi terkena pajak. Harga rata-rata dihitung dari total belanja dibagi total qty agar perubahan harga mudah dianalisis.
                  </InlineHint>

                  {bulananKategoriSections.length === 0 ? (
                    <PageEmptyState
                      compact
                      title="Belum ada belanja per kategori"
                      description="Tidak ada transaksi barang masuk pada periode bulanan yang dipilih sehingga total kategori belum dapat ditampilkan."
                    />
                  ) : (
                    <div className="space-y-4">
                      {bulananKategoriSections.map((section) => {
                        const KategoriIcon = getKategoriIcon(section.nama_kategori);
                        const kategoriTheme = getKategoriTheme(section.nama_kategori);
                        const kategoriKey = section.kategori_id || section.nama_kategori || "tanpa-kategori";
                        const isExpanded = activeBulananKategori === kategoriKey;

                        return (
                          <div
                            key={`kategori-belanja-${kategoriKey}`}
                            className={`rounded-2xl border bg-white/95 p-4 shadow-sm md:p-5 ${kategoriTheme.cardBorder}`}
                          >
                            <button
                              type="button"
                              onClick={() => toggleBulananKategori(kategoriKey)}
                              aria-expanded={isExpanded}
                              aria-controls={`kategori-belanja-panel-${kategoriKey}`}
                              className={`w-full rounded-2xl border px-4 py-4 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${kategoriTheme.headerBg}`}
                            >
                              <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
                                <div className="min-w-0 space-y-3">
                                  <div className="flex flex-wrap items-center gap-3">
                                    <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white/80 shadow-sm">
                                      {isExpanded ? (
                                        <ChevronDown size={18} className={kategoriTheme.iconText} />
                                      ) : (
                                        <ChevronRight size={18} className={kategoriTheme.iconText} />
                                      )}
                                    </span>
                                    <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white/75 shadow-sm">
                                      <KategoriIcon size={18} className={kategoriTheme.iconText} />
                                    </span>
                                    <div className="min-w-0">
                                      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                                        Kategori Belanja
                                      </p>
                                      <p className={`font-manrope text-xl font-bold ${kategoriTheme.titleText} break-words`}>
                                        {section.nama_kategori}
                                      </p>
                                    </div>
                                  </div>
                                  <div className="flex flex-wrap gap-2 text-xs font-medium text-slate-700">
                                    <span className={`rounded-full px-3 py-1 ${kategoriTheme.badgeClass}`}>
                                      {section.jumlah_transaksi} transaksi
                                    </span>
                                    <span className="rounded-full border border-white/70 bg-white/80 px-3 py-1">
                                      {section.jumlah_barang_unik} barang unik
                                    </span>
                                    <span className="rounded-full border border-white/70 bg-white/80 px-3 py-1">
                                      {section.jumlah_item_baris} baris pembelian
                                    </span>
                                  </div>
                                </div>
                                <div className="flex flex-col gap-2 xl:items-end">
                                  <div>
                                    <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500 xl:text-right">
                                      Total Belanja
                                    </p>
                                    <p className="font-manrope text-2xl font-bold text-slate-900 break-words xl:text-right">
                                      {formatRupiah(getDisplayMasukCategorySectionTotalBelanja(section.items || []))}
                                    </p>
                                  </div>
                                  <span className="inline-flex w-fit items-center rounded-full border border-white/70 bg-white/85 px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm">
                                    {isExpanded ? "Sembunyikan detail barang" : "Lihat detail barang"}
                                  </span>
                                </div>
                              </div>
                            </button>

                            {isExpanded && section.items?.length ? (
                              <div id={`kategori-belanja-panel-${kategoriKey}`} className="mt-4 overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-sm">
                                <div className="flex flex-col gap-2 border-b border-slate-200 bg-slate-50/80 px-4 py-3 md:flex-row md:items-center md:justify-between">
                                  <div>
                                    <p className="font-manrope text-sm font-bold text-slate-900">
                                      Daftar Barang {section.nama_kategori}
                                    </p>
                                    <p className="text-xs text-muted-foreground">
                                      Semua barang pada kategori ini ditampilkan lengkap dengan total kuantitas, harga rata-rata, dan total harganya.
                                    </p>
                                  </div>
                                  <span className="inline-flex w-fit items-center rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-700">
                                    {section.items.length} barang
                                  </span>
                                </div>

                                <div className="space-y-3 p-4 md:hidden">
                                  {section.items.map((item, itemIndex) => (
                                    <div
                                      key={`${kategoriKey}-${item.barang_id || item.nama_barang}-${item.satuan}`}
                                      className="rounded-xl border border-slate-200/80 bg-white p-4"
                                    >
                                      <div className="flex items-start justify-between gap-3">
                                        <div className="min-w-0 flex-1">
                                          <div className="flex items-center gap-2">
                                            <span className="inline-flex h-7 min-w-7 items-center justify-center rounded-full bg-slate-100 px-1.5 text-[11px] font-semibold text-slate-700">
                                              {itemIndex + 1}
                                            </span>
                                            <p className="font-manrope text-base font-semibold text-slate-900 break-words">
                                              {item.nama_barang}
                                            </p>
                                          </div>
                                          <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                                            <div>
                                              <p className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Total Qty</p>
                                              <p className="font-medium text-slate-900">
                                                {Number(item.total_qty || 0).toLocaleString("id-ID", { maximumFractionDigits: 2 })} {item.satuan}
                                              </p>
                                            </div>
                                            <div>
                                              <p className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Harga Rata-rata</p>
                                              <p className="font-medium text-slate-900">
                                                {formatRupiah(getDisplayMasukCategoryAverageHarga(item))}
                                              </p>
                                            </div>
                                          </div>
                                        </div>
                                        <div className="shrink-0 text-right">
                                          <p className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Total Harga</p>
                                          <p className="mt-1 font-manrope text-base font-bold text-slate-900">
                                            {formatRupiah(Number(item.total_belanja || 0))}
                                          </p>
                                          <div className="mt-3">
                                            <p className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Baris Pembelian</p>
                                            <p className="font-medium text-slate-900">{item.jumlah_item_baris}</p>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>

                                <div className="hidden overflow-x-auto md:block">
                                  <table className="min-w-[960px] w-full text-sm">
                                    <thead>
                                      <tr className={`${kategoriTheme.tableHeadBg} border-b border-slate-200`}>
                                        <th className="px-4 py-3 text-left text-xs font-manrope font-semibold uppercase tracking-wider text-muted-foreground">No</th>
                                        <th className="px-4 py-3 text-left text-xs font-manrope font-semibold uppercase tracking-wider text-muted-foreground">Nama Barang</th>
                                        <th className="px-4 py-3 text-left text-xs font-manrope font-semibold uppercase tracking-wider text-muted-foreground">Total Qty</th>
                                        <th className="px-4 py-3 text-left text-xs font-manrope font-semibold uppercase tracking-wider text-muted-foreground">Harga Rata-rata</th>
                                        <th className="px-4 py-3 text-right text-xs font-manrope font-semibold uppercase tracking-wider text-muted-foreground">Total Harga</th>
                                        <th className="px-4 py-3 text-left text-xs font-manrope font-semibold uppercase tracking-wider text-muted-foreground">Baris Pembelian</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {section.items.map((item, itemIndex) => (
                                        <tr
                                          key={`${kategoriKey}-${item.barang_id || item.nama_barang}-${item.satuan}`}
                                          className="border-b border-slate-100 transition-colors hover:bg-slate-50/70"
                                        >
                                          <td className="px-4 py-3 align-top font-medium text-slate-600">{itemIndex + 1}</td>
                                          <td className="px-4 py-3 align-top font-medium text-slate-900 break-words">{item.nama_barang}</td>
                                          <td className="px-4 py-3 align-top text-slate-700">
                                            {Number(item.total_qty || 0).toLocaleString("id-ID", { maximumFractionDigits: 2 })} {item.satuan}
                                          </td>
                                          <td className="px-4 py-3 align-top text-slate-700">
                                            {formatRupiah(getDisplayMasukCategoryAverageHarga(item))}
                                          </td>
                                          <td className="px-4 py-3 align-top text-right font-manrope font-bold text-slate-900">
                                            {formatRupiah(Number(item.total_belanja || 0))}
                                          </td>
                                          <td className="px-4 py-3 align-top text-slate-700">{item.jumlah_item_baris}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ) : (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50/70 p-6 shadow-sm space-y-5">
                  <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                    <div>
                      <h3 className="font-manrope font-bold text-xl">Barang Masuk</h3>
                      <p className="text-sm text-muted-foreground">
                        Ditampilkan berdasarkan filter mode, supplier, dan periode bulanan yang dipilih.
                      </p>
                    </div>
                    <span className="text-sm font-medium text-emerald-800">
                      {laporanBulanan.summary.jumlah_transaksi_masuk} transaksi
                    </span>
                  </div>

                  {laporanBulanan.barang_masuk_sections.length === 0 ? (
                    <PageEmptyState
                      compact
                      title="Tidak ada transaksi barang masuk"
                      description="Belum ada transaksi barang masuk yang sesuai dengan filter bulanan yang dipilih."
                    />
                  ) : (
                    <div className="space-y-4">
                      {laporanBulanan.barang_masuk_sections.map((section) => (
                        <div key={`masuk-${section.tanggal}`} className="rounded-xl border border-emerald-200/80 bg-white/90 p-4 shadow-sm space-y-4">
                          <div className="flex flex-col gap-2 border-b border-emerald-100 pb-3 md:flex-row md:items-center md:justify-between">
                            <div>
                              <p className="font-manrope font-bold text-lg text-slate-900">{section.tanggal_label || formatDateId(section.tanggal)}</p>
                              <p className="text-sm text-muted-foreground">{section.jumlah_transaksi} transaksi barang masuk</p>
                            </div>
                            <p className="text-sm font-semibold text-emerald-800">
                              Total {formatRupiah(getDisplayMasukSectionTotalBelanja(section.supplier_sections || []))}
                            </p>
                          </div>

                          <div className="space-y-4">
                            {section.supplier_sections.map((supplierSection) => (
                              <div key={`supplier-${section.tanggal}-${supplierSection.supplier_id || supplierSection.supplier_nama}`} className="rounded-xl border border-sky-200/80 bg-sky-50/60 p-4 shadow-sm space-y-3">
                                <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                                  <div>
                                    <p className="font-manrope font-bold text-base text-slate-900">{supplierSection.supplier_nama || "Tanpa Supplier"}</p>
                                    <p className="text-sm text-muted-foreground">{supplierSection.jumlah_transaksi} transaksi - {supplierSection.transaksi.reduce((total, trx) => total + (trx.items?.length || 0), 0)} item baris</p>
                                  </div>
                                  <p className="text-sm font-semibold text-sky-800">
                                    Total {formatRupiah((supplierSection.transaksi || []).reduce((total, transaksiItem) => total + getDisplayMasukTotalBelanja(transaksiItem), 0))}
                                  </p>
                                </div>

                                <div className="space-y-3">
                                  {supplierSection.transaksi.map((transaksiItem, idx) => (
                                    <button
                                      key={transaksiItem.pembelian_id || `bulanan-masuk-${section.tanggal}-${idx}`}
                                      type="button"
                                      onClick={() => handleOpenTransaksiDetail("masuk", transaksiItem)}
                                      className="w-full rounded-md border border-emerald-200 bg-emerald-50/60 p-4 text-left transition-colors hover:bg-emerald-100/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                                    >
                                      <div className="flex items-start justify-between gap-4">
                                        <div>
                                          <p className="font-medium mb-1">Total: {formatRupiah(getDisplayMasukTotalBelanja(transaksiItem))}</p>
                                          <div className="text-sm text-muted-foreground">
                                            {transaksiItem.items?.length || 0} item - Petugas: {transaksiItem.petugas || "-"}
                                          </div>
                                          <div className="text-sm text-muted-foreground">
                                            Supplier: {transaksiItem.supplier_nama || getSupplierName(transaksiItem.supplier_id)}
                                          </div>
                                          <div className="text-xs text-muted-foreground mt-1">
                                            {formatDateTimeId(transaksiItem.tanggal)}
                                          </div>
                                          {transaksiItem.keterangan && (
                                            <div className="text-xs text-muted-foreground mt-1 whitespace-pre-line">
                                              Catatan: {transaksiItem.keterangan}
                                            </div>
                                          )}
                                        </div>
                                        <div className="flex flex-col items-end gap-2">
                                          <div className="flex flex-wrap items-center justify-end gap-2">
                                            <SpjBadge spjStatus={transaksiItem.spj_status} />
                                            <ProofStatusBadge hasProof={getTransactionProofImageUrls(transaksiItem).length > 0} />
                                          </div>
                                          <span className="text-xs font-semibold text-emerald-700">Lihat Detail</span>
                                        </div>
                                      </div>
                                    </button>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <InlineHint className="border-orange-200 bg-orange-50/80 text-orange-950">
                Detail transaksi barang keluar bulanan sekarang dibuka pada halaman terpisah agar fokus pembacaan lebih jelas. Klik kartu <span className="font-semibold">Transaksi Barang Keluar</span> untuk melihat daftarnya.
              </InlineHint>
            </div>
          )}
        </TabsContent>

        <TabsContent value="tahunan" className="space-y-6">
          <div className="rounded-2xl border bg-card/95 p-6 shadow-sm">
            <h2 className="font-manrope font-bold text-xl mb-4">Filter Laporan Tahunan</h2>
            <InlineHint className="mb-4 border-slate-200 bg-slate-50/80">
              Laporan tahunan menampilkan rekap per bahan makanan sepanjang tahun. Pada layar kecil, gunakan kartu ringkas atau geser tabel bila perlu.
            </InlineHint>
            <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(360px,420px)] xl:items-end">
                <div className="space-y-2">
                  <Label>Tahun</Label>
                  <Input
                  data-testid="input-tahun-tahunan"
                  type="number"
                  value={tahun}
                  onChange={(e) => setTahun(parseInt(e.target.value))}
                />
              </div>
              <ReportActionPanel
                title="Aksi Laporan Tahunan"
                description="Tampilkan rekap tahunan atau export ke Excel untuk pengolahan lanjutan."
                tone="slate"
              >
                <Button onClick={fetchLaporanTahunan} disabled={loading}>
                  <FileText size={16} className="mr-2" />
                  Tampilkan Laporan
                </Button>
                <Button variant="outline" onClick={handleExportTahunanExcel} disabled={exporting}>
                  <Download size={16} className="mr-2" />
                  Export Excel
                </Button>
              </ReportActionPanel>
            </div>
          </div>

          {laporanTahunan && (
            <div className="rounded-lg border bg-card p-6 shadow-sm space-y-5">
              <div className="space-y-3">
                <h3 className="font-manrope font-bold text-xl">{tahunanTitle}</h3>
                <InlineHint className="border-slate-200 bg-slate-50/80">
                  {tahunanDescription}
                </InlineHint>
              </div>

              {laporanTahunan.kategori_data.length === 0 ? (
                <PageEmptyState
                  compact
                  title="Belum ada kategori tahunan"
                  description="Data tahunan belum memiliki kategori untuk ditampilkan."
                />
              ) : (
                <Tabs value={activeTahunanKategori} onValueChange={setActiveTahunanKategori} className="space-y-5">
                  <div className="overflow-x-auto pb-1">
                    <TabsList className="inline-flex h-auto min-w-max items-stretch justify-start gap-2 rounded-2xl border border-slate-200 bg-white/95 p-2">
                      <TabsTrigger
                        value="all"
                        className="flex min-h-11 items-center gap-2 rounded-xl border border-transparent px-4 py-2.5 text-slate-700 data-[state=active]:border-slate-900 data-[state=active]:bg-slate-900 data-[state=active]:text-white data-[state=active]:shadow-sm"
                      >
                        <FileText size={16} />
                        <span className="whitespace-nowrap">Semua Kategori</span>
                      </TabsTrigger>
                      {laporanTahunan.kategori_data.map((kategori) => {
                        const KategoriIcon = getKategoriIcon(kategori.nama_kategori);
                        return (
                          <TabsTrigger
                            key={kategori.kategori_id}
                            value={kategori.kategori_id}
                            className="flex min-h-11 items-center gap-2 rounded-xl border border-transparent px-4 py-2.5 text-slate-700 data-[state=active]:border-slate-900 data-[state=active]:bg-slate-900 data-[state=active]:text-white data-[state=active]:shadow-sm"
                          >
                            <KategoriIcon size={16} />
                            <span className="whitespace-nowrap">{kategori.nama_kategori}</span>
                          </TabsTrigger>
                        );
                      })}
                    </TabsList>
                  </div>

                  <TabsContent value={activeTahunanKategori} className="space-y-5 mt-0" key={activeTahunanKategori}>
                    <div className="space-y-5 md:hidden">
                      {tahunanKategoriTampil.map((kategori) => {
                        const KategoriIcon = getKategoriIcon(kategori.nama_kategori);
                        const kategoriTheme = getKategoriTheme(kategori.nama_kategori);

                        return (
                          <div key={kategori.kategori_id} className="space-y-3">
                            <div className={`rounded-xl border px-4 py-3 ${kategoriTheme.laporanRowBg || kategoriTheme.tableHeadBg}`}>
                              <div className="flex items-center gap-2">
                                <KategoriIcon size={17} className={kategoriTheme.iconText} />
                                <span className={`font-manrope font-bold uppercase ${kategoriTheme.titleText}`}>
                                  {kategori.nama_kategori}
                                </span>
                              </div>
                            </div>
                            {kategori.items.map((item) => (
                              <div key={`${kategori.kategori_id}-${item.barang_id}`} className="rounded-xl border border-slate-200 bg-white/90 p-4 shadow-sm">
                                <div className="flex items-start justify-between gap-3">
                                  <div>
                                    <p className="font-medium text-slate-900">{item.nama_barang}</p>
                                    <p className="text-sm text-muted-foreground">Satuan: {item.satuan}</p>
                                  </div>
                                  <span className="text-sm font-semibold text-primary">
                                    Total {Number(item.jumlah || 0).toLocaleString("id-ID")}
                                  </span>
                                </div>
                                <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                                  {laporanTahunan.months.map((m) => (
                                    <div key={`${item.barang_id}-${m}`} className="rounded-lg bg-slate-50 px-3 py-2">
                                      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{m}</p>
                                      <p className="font-medium">{Number(item[m] || 0).toLocaleString("id-ID")}</p>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        );
                      })}
                    </div>

                    <div className="hidden overflow-x-auto rounded-xl border border-slate-200/80 bg-white/80 md:block">
                      <table className="min-w-[1200px] w-full text-sm">
                        <thead>
                          <tr className="border-b bg-white">
                            <th className="px-3 py-2 text-left">No</th>
                            <th className="px-3 py-2 text-left">Bahan Makanan</th>
                            <th className="px-3 py-2 text-left">Satuan</th>
                            {laporanTahunan.months.map((m) => (
                              <th key={m} className="px-3 py-2 text-right uppercase">{m}</th>
                            ))}
                            <th className="px-3 py-2 text-right">Jumlah</th>
                          </tr>
                        </thead>
                        <tbody>
                          {tahunanKategoriTampil.flatMap((kategori) => {
                            const KategoriIcon = getKategoriIcon(kategori.nama_kategori);
                            const kategoriTheme = getKategoriTheme(kategori.nama_kategori);
                            const detailColSpan = (laporanTahunan.months?.length || 0) + 2;

                            return [
                              <tr
                                key={`kat-${kategori.kategori_id}`}
                                className={`${kategoriTheme.laporanRowBg || kategoriTheme.tableHeadBg} border-b`}
                              >
                                <td className="px-3 py-2"></td>
                                <td className="px-3 py-2">
                                  <div className="flex items-center gap-2">
                                    <KategoriIcon size={17} className={kategoriTheme.iconText} />
                                    <span className={`font-manrope font-bold text-base md:text-[1.05rem] uppercase ${kategoriTheme.titleText}`}>
                                      {kategori.nama_kategori}
                                    </span>
                                  </div>
                                </td>
                                <td className="px-3 py-2" colSpan={detailColSpan}></td>
                              </tr>,
                              ...kategori.items.map((item) => (
                                <tr key={`${kategori.kategori_id}-${item.barang_id}`} className="border-b hover:bg-muted/30">
                                  <td className="px-3 py-2">{item.no}</td>
                                  <td className="px-3 py-2">{item.nama_barang}</td>
                                  <td className="px-3 py-2">{item.satuan}</td>
                                  {laporanTahunan.months.map((m) => (
                                    <td key={`${item.barang_id}-${m}`} className="px-3 py-2 text-right">
                                      {Number(item[m] || 0).toLocaleString("id-ID")}
                                    </td>
                                  ))}
                                  <td className="px-3 py-2 text-right font-medium">
                                    {Number(item.jumlah || 0).toLocaleString("id-ID")}
                                  </td>
                                </tr>
                              )),
                            ];
                          })}
                        </tbody>
                      </table>
                    </div>
                  </TabsContent>
                </Tabs>
              )}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={detailDialogOpen} onOpenChange={handleDetailDialogChange}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedTransaksiType === "masuk"
                ? "Detail Barang Masuk"
                : selectedTransaksiType === "keluar"
                  ? "Detail Barang Keluar"
                  : "Detail Transaksi"}
            </DialogTitle>
          </DialogHeader>

          {selectedTransaksiDetail && selectedTransaksiType === "masuk" && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Supplier</p>
                  <p className="font-manrope font-semibold">
                    {getSupplierName(selectedTransaksiDetail.supplier_id)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Tanggal</p>
                  <p className="font-medium">{formatDateTimeId(selectedTransaksiDetail.tanggal)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Petugas</p>
                  <p className="font-medium">{selectedTransaksiDetail.petugas || "-"}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Status</p>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    {selectedTransaksiDetail.status || "-"}
                  </span>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Status SPJ</p>
                  <div className="mt-1 flex flex-wrap items-center gap-2">
                    <SpjBadge spjStatus={selectedTransaksiDetail.spj_status} />
                    <ProofStatusBadge hasProof={selectedTransaksiFotoGalleryItems.length > 0} />
                  </div>
                </div>
                {selectedTransaksiTaxBreakdown && (
                  <>
                    <div>
                      <p className="text-sm text-muted-foreground">Status NPWP Supplier</p>
                      <p className="font-medium">
                        {selectedTransaksiTaxBreakdown.supplierHasNpwp
                          ? "Memiliki NPWP"
                          : "Tidak memiliki NPWP"}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Tarif Pajak</p>
                      <p className="font-medium">
                        {selectedTransaksiTaxBreakdown.tarifPajakPersen.toLocaleString("id-ID")}%
                      </p>
                    </div>
                  </>
                )}
                {selectedTransaksiDetail.bukti_nota && (
                  <div>
                    <p className="text-sm text-muted-foreground">No. Bukti/Nota</p>
                    <p className="font-medium">{selectedTransaksiDetail.bukti_nota}</p>
                  </div>
                )}
                {selectedTransaksiDetail.keterangan && (
                  <div className="md:col-span-2">
                    <p className="text-sm text-muted-foreground">Catatan</p>
                    <p className="font-medium whitespace-pre-line">{selectedTransaksiDetail.keterangan}</p>
                  </div>
                )}
              </div>

              {selectedTransaksiFotoGalleryItems.length > 0 ? (
                <ProofImageGallery
                  title={`Foto Bukti (${selectedTransaksiFotoGalleryItems.length})`}
                  hint="Klik gambar untuk membuka ukuran penuh. Semua bukti ditampilkan dengan ukuran thumbnail yang konsisten."
                  images={selectedTransaksiFotoGalleryItems}
                />
              ) : null}

              <div>
                <p className="text-sm font-manrope font-semibold mb-2">
                  Daftar Item ({(selectedTransaksiDetail.items || []).length})
                </p>
                <div className="border rounded-md overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="px-3 py-2 text-center w-14">No</th>
                        <th className="px-3 py-2 text-left">Barang</th>
                        <th className="px-3 py-2 text-center">Qty</th>
                        <th className="px-3 py-2 text-right">Harga</th>
                        <th className="px-3 py-2 text-right">Subtotal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedTransaksiDisplayItems.map((item, idx) => (
                        <tr key={idx} className="border-t">
                          <td className="px-3 py-2 text-center font-medium">{idx + 1}</td>
                          <td className="px-3 py-2">{getBarangName(item.barang_id)}</td>
                          <td className="px-3 py-2 text-center">{getItemQtyWithSatuan(item)}</td>
                          <td className="px-3 py-2 text-right">
                            {formatRupiah(Number(item.harga_satuan ?? 0))}
                          </td>
                          <td className="px-3 py-2 text-right">
                            {formatRupiah(Number(item.subtotal ?? 0))}
                          </td>
                        </tr>
                      ))}
                      {selectedTransaksiTaxBreakdown && (
                        <>
                          <tr className="border-t bg-muted/20">
                            <td colSpan="4" className="px-3 py-2 text-right font-medium">
                              SUBTOTAL SEBELUM PAJAK:
                            </td>
                            <td className="px-3 py-2 text-right font-medium">
                              {formatRupiah(selectedTransaksiDisplaySummary.subtotalSebelumPajak)}
                            </td>
                          </tr>
                          <tr className="border-t bg-muted/20">
                            <td colSpan="4" className="px-3 py-2 text-right font-medium">
                              PAJAK ({selectedTransaksiDisplaySummary.tarifPajakPersen.toLocaleString("id-ID")}%)
                            </td>
                            <td className="px-3 py-2 text-right font-medium">
                              {formatRupiah(selectedTransaksiDisplaySummary.nilaiPajak)}
                            </td>
                          </tr>
                          <tr className="border-t font-manrope font-bold bg-muted/30">
                            <td colSpan="4" className="px-3 py-2 text-right">
                              TOTAL BELANJA:
                            </td>
                            <td className="px-3 py-2 text-right text-primary">
                              {formatRupiah(selectedTransaksiDisplaySummary.totalBelanja)}
                            </td>
                          </tr>
                        </>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="flex flex-wrap justify-end gap-2">
                {canManageSpj && (
                  <Button
                    type="button"
                    variant={selectedTransaksiDetail.spj_status ? "destructive" : "default"}
                    onClick={() => handleToggleSpjStatus(selectedTransaksiDetail)}
                    disabled={spjUpdatingId === selectedTransaksiDetail.pembelian_id}
                  >
                    {spjUpdatingId === selectedTransaksiDetail.pembelian_id ? (
                      "Memproses..."
                    ) : selectedTransaksiDetail.spj_status ? (
                      "Batal SPJ"
                    ) : (
                      "Tandai SPJ"
                    )}
                  </Button>
                )}
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleDownloadNota(selectedTransaksiDetail)}
                  disabled={
                    !selectedTransaksiDetail.pembelian_id ||
                    downloadingNotaId === selectedTransaksiDetail.pembelian_id
                  }
                >
                  <Download size={16} className="mr-2" />
                  {downloadingNotaId === selectedTransaksiDetail.pembelian_id
                    ? "Mendownload..."
                    : "Download Nota"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handlePrintNota(selectedTransaksiDetail.pembelian_id)}
                  disabled={
                    !selectedTransaksiDetail.pembelian_id ||
                    printingNotaId === selectedTransaksiDetail.pembelian_id
                  }
                >
                  <Printer size={16} className="mr-2" />
                  {printingNotaId === selectedTransaksiDetail.pembelian_id
                    ? "Menyiapkan..."
                    : "Print Nota"}
                </Button>
              </div>
            </div>
          )}

          {selectedTransaksiDetail && selectedTransaksiType === "keluar" && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Tanggal</p>
                  <p className="font-medium">{formatDateTimeId(selectedTransaksiDetail.tanggal)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Petugas</p>
                  <p className="font-medium">{selectedTransaksiDetail.petugas || "-"}</p>
                </div>
                {selectedTransaksiDetail.keterangan && (
                  <div className="col-span-2">
                    <p className="text-sm text-muted-foreground">Keterangan</p>
                    <p className="font-medium">{selectedTransaksiDetail.keterangan}</p>
                  </div>
                )}
              </div>

              {selectedTransaksiFotoGalleryItems.length > 0 ? (
                <ProofImageGallery
                  title={`Foto Bukti (${selectedTransaksiFotoGalleryItems.length})`}
                  hint="Klik gambar untuk membuka ukuran penuh. Semua bukti ditampilkan dengan ukuran thumbnail yang konsisten."
                  images={selectedTransaksiFotoGalleryItems}
                />
              ) : null}

              <div>
                <p className="text-sm font-manrope font-semibold mb-2">
                  Daftar Item ({(selectedTransaksiDetail.items || []).length})
                </p>
                <div className="border rounded-md overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="px-3 py-2 text-center w-14">No</th>
                        <th className="px-3 py-2 text-left">Barang</th>
                        <th className="px-3 py-2 text-center">Qty</th>
                        <th className="px-3 py-2 text-left">Keterangan</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(selectedTransaksiDetail.items || []).map((item, idx) => (
                        <tr key={idx} className="border-t">
                          <td className="px-3 py-2 text-center font-medium">{idx + 1}</td>
                          <td className="px-3 py-2">{getBarangName(item.barang_id)}</td>
                          <td className="px-3 py-2 text-center">{getItemQtyWithSatuan(item)}</td>
                          <td className="px-3 py-2">{item.keterangan || "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </PageShell>
  );
};

export default LaporanPage;
